/* Copyright (c) 2020-present CloudIO Inc */

import { Item } from 'componentregistry';
import { ItemType } from '../../registry/Base.type';
import { ComponentSample } from '../../registry/Component.type';

const page: {
  root: Item<'Page'>;
  flexPanel: Item<'FlexPanel'>;
  flexItem: Item<'FlexItem'>;
  typography: Item<'Typography'>;
  box: Item<'Box'>;
  box1: Item<'Box'>;
} = {
  box: {
    children: ['box1'],
    itemId: 'box',
    parent: 'flexItem',
    props: {
      border: {
        all: {
          style: 'solid',
          width: 0,
        },
      },
      boxHeight: '100%',
    },
    type: 'Box',
  },
  box1: {
    children: ['typography'],
    itemId: 'box1',
    parent: 'box',
    props: {
      boxWidth: '80px',
    },
    type: 'Box',
  },
  flexItem: {
    children: ['box'],
    itemId: 'flexItem',
    parent: 'flexPanel',
    props: {},
    type: 'FlexItem',
  },
  flexPanel: {
    children: ['flexItem'],
    itemId: 'flexPanel',
    parent: 'root',
    props: {},
    type: 'FlexPanel',
  },
  root: {
    children: ['flexPanel'],
    itemId: 'root',
    parent: 'root',
    props: {
      windowTitle: {
        type: 'text',
        value: 'Blank Page',
      },
    },
    type: 'Page',
  },
  typography: {
    children: [],
    itemId: 'typography',
    parent: 'box1',
    props: {
      color: {
        type: 'text',
        value: 'rgba(189,16,224,1)',
      },
      fontSize: '20px',
      fontStyle: 'italic',
      fontWeight: 500,
      noWrap: true,
      text: {
        type: 'text',
        value: 'Sample Text',
      },
    },
    type: 'Typography',
  },
};

// append the below to the existing helpRenders
export const helpRenders = <
  T extends Extract<
    ItemType,
    'Typography' | 'H1' | 'H2' | 'H3' | 'H4' | 'H5' | 'H6'
  >,
>(
  itemType: T,
): ComponentSample<T>[] => {
  const type = itemType as 'Typography';
  const renders: ComponentSample<'Typography'>[] = [
    {
      title: `Basic ${type}`,
      page,
      exampleId: 'eg1',
      clearParentChildren: true,
      height: 120,
      visibleProps: ['text'],
      item: {
        itemId: 'typography',
        type,
        children: [],
        parent: 'box',
        props: {
          text: {
            value: 'Sample Text',
            type: 'text',
          },
        },
      },
    },
    {
      title: `${type} w/ Expression`,
      page,
      exampleId: 'eg2',
      clearParentChildren: true,
      height: 120,
      visibleProps: ['text'],
      item: {
        itemId: 'typography',
        type,
        children: [],
        parent: 'box',
        props: {
          text: {
            // eslint-disable-next-line no-template-curly-in-string
            value: '`Hi ${$.session.displayName}`',
            type: 'expression',
          },
        },
      },
    },
    {
      title: `${type} w/ No Wrap`,
      description:
        "With the parent container's width fixed, try changing the No Wrap property.",
      page,
      exampleId: 'eg3',
      clearParentChildren: true,
      height: 220,
      visibleProps: ['text', 'noWrap', 'variant'],
      item: {
        itemId: 'typography',
        type,
        children: [],
        parent: 'box1',
        props: {
          text: {
            value: 'Sample Text',
            type: 'text',
          },
          noWrap: false,
          variant: 'body1',
        },
      },
    },
    {
      title: `${type} w/ Different Font Styles`,
      description: '',
      page,
      exampleId: 'eg4',
      clearParentChildren: true,
      height: 280,
      visibleProps: ['fontStyle', 'fontWeight', 'fontSize', 'color'],
      item: {
        itemId: 'typography',
        type,
        children: [],
        parent: 'box',
        props: {
          text: {
            value: 'Sample Text',
            type: 'text',
          },
          fontStyle: 'italic',
          fontWeight: 700,
          fontSize: '20px',
          color: {
            type: 'text',
            value: 'rgba(189,16,224,1)',
          },
        },
      },
    },
  ];
  return renders as unknown as ComponentSample<T>[];
};
